import { autobind } from 'core-decorators'
import { inject, observer } from 'mobx-react'
import * as React from 'react'
import { Button, ButtonBar, NextButton } from '../../../../shared/components/Button'
import { ContentContainerBody, ContentRow, SplitContentContainer } from '../../../../shared/components/Content'
import { Headlines, InnerContainer, Text } from '../../../../shared/components/Elements'
import { Headline, HeadlineCSS, SubHeadline } from '../../../../shared/components/Headlines'
import { RadioButtonControl } from '../../../../shared/components/RadioButtonControl'
import { SideInformation } from '../../../../shared/components/SideInformation'
import { Jaw } from '../../../../shared/Constants'
import { breakpoint, styled } from '../../../../shared/styled-components'
import { Utils } from '../../../../shared/Utils'
import { CalculatorStoreInstance } from '../../../../stores/CalculatorStore'
import { Routes } from '../../../Routes'
import { SegmentedProgressBar } from '../result/SegmentedProgressBar'

interface IProps {
  store?: CalculatorStoreInstance
  history?: History
  jaw: Jaw
}

@inject('store')
@observer
export class BoneGraftingTypeView extends React.Component<IProps> {
  // -------------------------------------------------------------------------- LIFECYCLE

  componentDidMount () {
    const { store, jaw } = this.props
    const requirement = store!.offer.getJawRequirement(jaw)
    if (requirement.boneGrafting === 'no') {
      const jawRoute = Routes.getJawRoute(jaw)
      store.redirectToRoute(jawRoute.subRoutes.boneGrafting.subRoutes.requirements)
    }
  }

  // -------------------------------------------------------------------------- EVENTS

  @autobind
  private _onBoneGraftingTypeChange (boneGraftingType: 'sinus_lifting_onesided'|'sinus_lifting_twosided'|'normal') {
    const { store, jaw } = this.props
    const requirement = store!.offer.getJawRequirement(jaw)
    requirement.setBoneGraftingType(boneGraftingType)
  }

  // -------------------------------------------------------------------------- RENDERING

  render () {
    const { store, jaw } = this.props
    const requirement = store!.offer.getJawRequirement(jaw)

    if (!requirement) return <div />

    return (<InnerContainer padded>
      <Headlines>
        <Headline centered>Knochenaufbau</Headline>
        <SubHeadline centered>Welche Knochenaufbau-Variante benötigen Sie?</SubHeadline>
      </Headlines>
      <SplitContentContainer>
        <ContentContainerBody vertical>
          <ContentRow>
            <Image src={Utils.getAssetPath('/assets/calculator/bone_grafting_type.png')} />
          </ContentRow>
          <ContentRow paddedVertically>
            <ContentText>
              <ContentTextBlock>Ein Sinuslift kommt zum Einsatz, wenn der Knochen nicht „hoch genug“ ist, um ein Implantat aufzunehmen. Dieser Fall tritt in der Regel nur im Oberkieferseitenzahnbereich ein. Wenn Sie der Meinung sind, dass Sie sowohl links als auch rechts einen Sinuslift für Ihre Implantatversorgung benötigen, dann wählen Sie „beidseitig“ aus.</ContentTextBlock>
              <ContentTextBlock>Bei einem Basis-Knochenaufbau wird Knochenersatzmaterial oder körpereigenes Knochenmaterial dazu verwendet, den Kieferkamm vor oder während der Implantation aufzubauen. Nur mit ausreichend Knochensubstanz kann das Implantat ausreichend stabilisiert werden und vollständig einheilen.</ContentTextBlock>
            </ContentText>
            <Options>
              <OptionHeadline>Knochenaufbau-Variante</OptionHeadline>
              <RadioButtons>
                <RadioButton
                  label='Sinuslift einseitig'
                  checked={requirement.boneGraftingType === 'sinus_lifting_onesided'}
                  onChange={this._onBoneGraftingTypeChange.bind(this, 'sinus_lifting_onesided')}
                  tooltip='Bei Ihnen wird nur auf einer Kieferseite (rechts oder links) im Oberkieferbereich ein Sinuslift notwendig.' />
                <RadioButton
                  label='Sinuslift beidseitig'
                  checked={requirement.boneGraftingType === 'sinus_lifting_twosided'}
                  onChange={this._onBoneGraftingTypeChange.bind(this, 'sinus_lifting_twosided')}
                  tooltip='Bei Ihnen wird auf beiden Kieferseiten (rechts und links) im Oberkieferbereich ein Sinuslift notwendig.' />
                <RadioButton
                  label='Normaler Knochenaufbau'
                  checked={requirement.boneGraftingType === 'normal'}
                  onChange={this._onBoneGraftingTypeChange.bind(this, 'normal')}
                  tooltip='Ihr Knochen muss im Unterkieferbereich oder priorisiert in der Breite aufgebaut werden.' />
              </RadioButtons>
            </Options>
          </ContentRow>
        </ContentContainerBody>
        <SideInformation
          onBackClick={() => store!.goBack()}
          onNextClick={() => store!.goForward()}
          infoText={jaw === 'upper'
            ? '85% der Implantate nach 20 Jahren noch funktionieren? Kein anderer Zahnersatz ist so zuverlässig und langlebig.'
            : 'Sie sich in unseren implacheck®-Praxen unverbindlich beraten und Ihre Zahnsituation analysieren lassen können?'
          }
          longInfoText={jaw === 'upper'
            ? '85% der Implantate nach 20 Jahren noch funktionieren? Kein anderer Zahnersatz ist so zuverlässig und langlebig. Die Verlustrate wird durch Faktoren wie Rauchen, Diabetes, schwere unbehandelte Parodontitis und schwierige anatomische Strukturen erhöht. Lassen Sie in einem persönlichen Gespräch mit Ihrem implacheck®-Experten gerne eine individuelle Risikoanalyse vornehmen. Wir freuen uns Sie in einer unserer Praxen begrüßen zu dürfen.'
            : 'Sie sich in unseren implacheck®-Praxen unverbindlich beraten und Ihre Zahnsituation analysieren lassen können? Nachdem Sie unseren Rechner durchlaufen haben stellen wir Ihnen mehrere implacheck®-Experten in Ihrer Nähe vor. Indem Sie sich Ihre Kalkulation per Mail zusenden lassen oder Ihrem Behandler Ihren persönlichen Implacode nennen, können Sie Ihre Zahnsituation mit Ihren persönlichen Angaben abgleichen und erhalten eine individuelle Beratung. Kostenlos und unverbindlich.'
          }/>
      </SplitContentContainer>

      <ButtonBar>
        <Button onClick={() => store!.goBack()}>Zurück</Button>
        <SegmentedProgressBar segmentCount={2} segment={1} />
        <NextButton primary onClick={() => store!.goForward()}>Weiter</NextButton>
      </ButtonBar>
    </InnerContainer>)
  }
}

const Image = styled.img`
  width: 100%;
`

const ContentText = styled(Text)`
  flex: 1;
`

const ContentTextBlock = styled.p`
  margin-bottom: 1em;
`

const Options = styled.div`
  flex: 0;
  margin-bottom: 20px;
  ${breakpoint('tablet')`
    flex: 0 0 250px;
    padding-left: 40px;
    margin-bottom: 0;
  `}
`

const RadioButtons = styled.div`
  display: flex;
  flex-direction: column;
`

const RadioButton = styled(RadioButtonControl)`
  margin-bottom: 10px;  
  &:last-child {
    margin-bottom: 0;
  }
`

const OptionHeadline = styled.h4`
  ${HeadlineCSS};
  font-size: 20px !important; 
  margin-bottom: 15px;
`
